import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SoftwareUpdate } from '../../models/software-update.model';
import { BatchAssignmentData } from '../../models/batch-assignment.model';
import { SoftwareUpdateService } from '../../services/software-update.service';
import { Modal } from '../../../../shared/components/modal/modal';
import { ToastService } from '../../../../shared/services/toast-service';
import { tenantAccent } from '../../../../shared/utils/tenant-color';

/** A tenant's slice of the current selection, as handed in from the Assign
 *  devices tab (mirrors AssignDevicesTabComponent's SelectedDevice grouping). */
export interface ConfirmTenantGroup {
  tenantId: string;
  tenantName: string;
  clientIds: string[];
}

/**
 * Confirm step (handoff screen 3b) — shown after "Continue to confirm".
 * Submits the real batch via `SoftwareUpdateService.assignBatch()`
 * (`POST .../assignments`). The wireframe's offline/already-current callout
 * boxes need pre-check data the current endpoint doesn't return (see the
 * "Missing API endpoints" list, item 3) — omitted for now rather than shown
 * with fabricated numbers.
 */
@Component({
  selector: 'app-confirm-assignment-dialog',
  standalone: true,
  imports: [Modal],
  templateUrl: './confirm-assignment-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmAssignmentDialogComponent {
  readonly update = input.required<SoftwareUpdate>();
  readonly groups = input.required<ConfirmTenantGroup[]>();

  readonly close = output<void>();
  /** Emitted once the batch call settles, whether or not every device succeeded. */
  readonly assigned = output<BatchAssignmentData>();

  private readonly service = inject(SoftwareUpdateService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly open = signal(true);
  readonly acknowledged = signal(false);
  readonly isSubmitting = signal(false);

  readonly totalCount = computed(() => this.groups().reduce((sum, g) => sum + g.clientIds.length, 0));
  readonly tenantCount = computed(() => this.groups().length);

  tenantDot(tenantId: string): string {
    return tenantAccent(tenantId).dot;
  }

  onClose(): void {
    this.close.emit();
  }

  submit(): void {
    if (!this.acknowledged() || this.isSubmitting()) return;
    this.isSubmitting.set(true);

    const clientIdsByTenant: Record<string, string[]> = {};
    for (const g of this.groups()) {
      clientIdsByTenant[g.tenantId] = g.clientIds;
    }

    this.service
      .assignBatch(this.update().id, { clientIdsByTenant })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const { assigned, failed, requested } = res.data;
          if (failed === 0) {
            this.toastService.show(`Software update assigned to ${assigned} devices`, 'success');
          } else {
            this.toastService.show(
              `Assigned to ${assigned} of ${requested} devices — ${failed} failed. See the rollout tab for details.`,
              'error',
            );
          }
          this.isSubmitting.set(false);
          this.assigned.emit(res.data);
        },
        error: () => {
          this.toastService.show('Failed to assign software update', 'error');
          this.isSubmitting.set(false);
        },
      });
  }
}
